import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Link2, Loader2, Search, ShieldAlert, ShieldCheck, Store, UserPlus, XCircle } from "lucide-react";
import api, { getApiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { confirmToast } from "../../lib/confirmToast";
import { formatDisplayValue, formatStatus } from "../../lib/display";

const statusClass = {
  ativo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pendente: "bg-amber-50 text-amber-700 border-amber-200",
  inativo: "bg-slate-100 text-slate-600 border-slate-200",
};

const isMasterAccount = (user) => user?.is_master || user?.tipo === "master";
const canManageStoreLinks = (user) => user?.tipo === "admin" && !isMasterAccount(user);
const isSuperiorAccount = (user) => canManageStoreLinks(user) && user?.admin_role !== "gerente";

const getAccountProfile = (user) => {
  if (isMasterAccount(user)) return "Administrador Master";
  if (user?.tipo === "admin" && user?.admin_role === "gerente") return "Gerente de loja";
  if (user?.tipo === "admin") return "Administrador superior";
  if (user?.tipo === "parceiro") return "Parceiro";
  return formatDisplayValue(user?.tipo);
};

const AdminContas = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [savingLinks, setSavingLinks] = useState(false);
  const [linkDialogUser, setLinkDialogUser] = useState(null);
  const [selectedLojaIds, setSelectedLojaIds] = useState([]);
  const [selectedFixedLojaId, setSelectedFixedLojaId] = useState("");
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [savingManager, setSavingManager] = useState(false);
  const [roleDialogUser, setRoleDialogUser] = useState(null);
  const [selectedAdminRole, setSelectedAdminRole] = useState("superior");
  const [savingAdminRole, setSavingAdminRole] = useState(false);
  const [managerForm, setManagerForm] = useState({
    nome: "",
    email: "",
    password: "",
    loja_ids: [],
  });

  const canCreateManagers = isSuperiorAccount(currentUser);

  const fetchUsers = async () => {
    try {
      const [usersResponse, lojasResponse] = await Promise.all([
        api.get("/users"),
        api.get("/lojas"),
      ]);
      setUsers(usersResponse.data);
      setLojas(lojasResponse.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar contas."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const updateStatus = async (user, status) => {
    try {
      setSavingId(user.id);
      await api.put(`/users/${user.id}/status`, { status });
      toast.success(`Conta ${formatStatus(status).toLowerCase()} com sucesso!`);
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao atualizar conta."));
    } finally {
      setSavingId(null);
    }
  };

  const confirmInactivate = (user) => {
    confirmToast({
      title: "Inativar conta?",
      description: `${user.nome} não conseguirá acessar o sistema até uma nova liberação.`,
      confirmLabel: "Inativar",
      variant: "danger",
      onConfirm: () => updateStatus(user, "inativo"),
    });
  };

  const lojasMap = useMemo(() => {
    return lojas.reduce((acc, loja) => {
      acc[loja.id] = loja;
      return acc;
    }, {});
  }, [lojas]);

  const getUserLojaIds = useCallback((user) => {
    if (isMasterAccount(user)) return lojas.map((loja) => loja.id);
    const ids = Array.isArray(user.loja_ids) ? user.loja_ids : [];
    const combined = new Set([...ids, user.loja_id].filter(Boolean));
    return Array.from(combined);
  }, [lojas]);

  const getStoreSearchText = useCallback((user) => {
    if (isMasterAccount(user)) return "todas as lojas";
    return getUserLojaIds(user)
      .map((lojaId) => lojasMap[lojaId]?.nome || lojaId)
      .join(" ")
      .toLowerCase();
  }, [getUserLojaIds, lojasMap]);

  const openStoreLinksDialog = (user) => {
    setLinkDialogUser(user);
    const lojaIds = getUserLojaIds(user);
    setSelectedLojaIds(lojaIds);
    setSelectedFixedLojaId(user.loja_id || lojaIds[0] || "");
  };

  const toggleLoja = (lojaId) => {
    setSelectedLojaIds((current) => {
      if (current.includes(lojaId)) {
        const next = current.filter((id) => id !== lojaId);
        if (selectedFixedLojaId === lojaId) {
          setSelectedFixedLojaId(next[0] || "");
        }
        return next;
      }
      return [...current, lojaId];
    });
  };

  const selectFixedStore = (lojaId) => {
    setSelectedFixedLojaId(lojaId);
    setSelectedLojaIds((current) => (
      current.includes(lojaId) ? current : [...current, lojaId]
    ));
  };

  const saveStoreLinks = async () => {
    if (!linkDialogUser) return;

    try {
      setSavingLinks(true);
      if (linkDialogUser.tipo === "parceiro") {
        if (!selectedFixedLojaId) {
          toast.error("Selecione a loja fixa do parceiro.");
          return;
        }
        await api.put(`/parceiros/${linkDialogUser.parceiro_id}/lojas`, {
          loja_id: selectedFixedLojaId,
          loja_ids: selectedLojaIds,
        });
      } else {
        await api.put(`/users/${linkDialogUser.id}/lojas`, {
          loja_ids: selectedLojaIds,
        });
      }
      toast.success("Vínculos de lojas atualizados!");
      setLinkDialogUser(null);
      setSelectedLojaIds([]);
      setSelectedFixedLojaId("");
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao atualizar vínculos de lojas."));
    } finally {
      setSavingLinks(false);
    }
  };

  const toggleManagerStore = (lojaId) => {
    setManagerForm((current) => ({
      ...current,
      loja_ids: current.loja_ids.includes(lojaId)
        ? current.loja_ids.filter((id) => id !== lojaId)
        : [...current.loja_ids, lojaId],
    }));
  };

  const createManager = async (event) => {
    event.preventDefault();
    try {
      setSavingManager(true);
      await api.post("/users/sub-administradores", managerForm);
      toast.success("Gerente liberado com sucesso!");
      setManagerDialogOpen(false);
      setManagerForm({ nome: "", email: "", password: "", loja_ids: [] });
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao liberar gerente."));
    } finally {
      setSavingManager(false);
    }
  };

  const openRoleDialog = (user) => {
    setRoleDialogUser(user);
    setSelectedAdminRole(user.admin_role || "superior");
  };

  const saveAdministrativeRole = async () => {
    if (!roleDialogUser) return;
    try {
      setSavingAdminRole(true);
      const response = await api.put(`/users/${roleDialogUser.id}/perfil-administrativo`, {
        admin_role: selectedAdminRole,
      });
      setUsers((current) => current.map((user) => (
        user.id === roleDialogUser.id ? response.data : user
      )));
      toast.success("Perfil administrativo atualizado!");
      setRoleDialogUser(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao atualizar perfil administrativo."));
    } finally {
      setSavingAdminRole(false);
    }
  };

  const canManageAccount = (user) => {
    if (isMasterAccount(currentUser)) return !isMasterAccount(user);
    if (user.id === currentUser?.id) return false;
    if (user.tipo === "admin") {
      return canCreateManagers && user.parent_admin_id === currentUser?.id;
    }
    return user.tipo === "parceiro";
  };

  const renderStoreLinks = (user) => {
    if (isMasterAccount(user)) {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          Todas as lojas
        </Badge>
      );
    }

    const lojaIds = getUserLojaIds(user);
    if (lojaIds.length === 0) {
      return <span className="text-sm text-slate-400">Nenhuma loja vinculada</span>;
    }

    return (
      <div className="flex max-w-md flex-wrap gap-2">
        {user.tipo === "parceiro" && user.loja_id && (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Fixa: {lojasMap[user.loja_id]?.nome || user.loja_id}
          </Badge>
        )}
        {lojaIds.map((lojaId) => (
          <Badge key={lojaId} variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
            <Store className="mr-1.5 h-3.5 w-3.5" />
            {lojasMap[lojaId]?.nome || lojaId}
          </Badge>
        ))}
      </div>
    );
  };

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.toLowerCase();
    return users.filter((user) => {
      return (
        user.nome?.toLowerCase().includes(normalizedSearch) ||
        user.email?.toLowerCase().includes(normalizedSearch) ||
        user.tipo?.toLowerCase().includes(normalizedSearch) ||
        user.status?.toLowerCase().includes(normalizedSearch) ||
        getStoreSearchText(user).includes(normalizedSearch)
      );
    });
  }, [search, users, getStoreSearchText]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-contas">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Contas</h1>
          <p className="text-slate-600">Autorize, bloqueie e acompanhe os acessos ao PIN Clube.</p>
        </div>
        {canCreateManagers && (
          <Button className="btn-primary" onClick={() => setManagerDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Liberar gerente
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar por nome, email, tipo ou status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Nenhuma conta encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Usuário</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Perfil</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Lojas vinculadas</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{user.nome}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {getAccountProfile(user)}
                    </td>
                    <td className="px-6 py-4">
                      {renderStoreLinks(user)}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className={statusClass[user.status] || statusClass.inativo}>
                        {formatDisplayValue(user.status)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        {canManageAccount(user) && (canManageStoreLinks(user) || user.tipo === "parceiro") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                            onClick={() => openStoreLinksDialog(user)}
                          >
                            <Link2 className="h-4 w-4 mr-2" />
                            Lojas
                          </Button>
                        )}
                        {isMasterAccount(currentUser) && canManageStoreLinks(user) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                            onClick={() => openRoleDialog(user)}
                          >
                            <ShieldCheck className="mr-2 h-4 w-4" />
                            Perfil
                          </Button>
                        )}
                        {user.status !== "ativo" && canManageAccount(user) && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={savingId === user.id}
                            onClick={() => updateStatus(user, "ativo")}
                          >
                            {savingId === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            <span className="ml-2">Ativar</span>
                          </Button>
                        )}
                        {user.status !== "inativo" && canManageAccount(user) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            disabled={savingId === user.id}
                            onClick={() => confirmInactivate(user)}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Inativar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!linkDialogUser} onOpenChange={(open) => !open && setLinkDialogUser(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{linkDialogUser?.tipo === "parceiro" ? "Vincular lojas ao parceiro" : "Vincular lojas"}</DialogTitle>
            <DialogDescription>
              {linkDialogUser?.tipo === "parceiro"
                ? "Defina a loja fixa para campanhas e as lojas nas quais as vendas podem gerar pontos."
                : "Defina quais lojas este gerente poderá gerenciar no sistema."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
              <p className="font-medium text-slate-900">{linkDialogUser?.nome}</p>
              <p className="text-sm text-slate-500">{linkDialogUser?.email}</p>
            </div>

            {linkDialogUser?.tipo === "parceiro" && (
              <div className="space-y-2">
                <Label htmlFor="partner-fixed-store">Loja fixa</Label>
                <select
                  id="partner-fixed-store"
                  value={selectedFixedLojaId}
                  onChange={(event) => selectFixedStore(event.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Selecione uma loja</option>
                  {lojas.map((loja) => (
                    <option key={loja.id} value={loja.id}>{loja.nome}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {lojas.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                  Nenhuma loja cadastrada.
                </div>
              ) : (
                lojas.map((loja) => {
                  const checked = selectedLojaIds.includes(loja.id);
                  return (
                    <Label
                      key={loja.id}
                      htmlFor={`loja-${loja.id}`}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 transition-colors hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`loja-${loja.id}`}
                        checked={checked}
                        disabled={linkDialogUser?.tipo === "parceiro" && loja.id === selectedFixedLojaId}
                        onCheckedChange={() => toggleLoja(loja.id)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-slate-900">{loja.nome}</span>
                        <span className="block text-sm text-slate-500">
                          {[loja.cidade, loja.estado].filter(Boolean).join(" - ") || "Localização não informada"}
                        </span>
                        {linkDialogUser?.tipo === "parceiro" && loja.id === selectedFixedLojaId && (
                          <span className="mt-1 block text-xs font-medium text-primary">Loja fixa</span>
                        )}
                      </span>
                    </Label>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLinkDialogUser(null)}
              disabled={savingLinks}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="btn-primary"
              onClick={saveStoreLinks}
              disabled={savingLinks}
            >
              {savingLinks ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              <span className="ml-2">Salvar vínculos</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={managerDialogOpen} onOpenChange={setManagerDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Liberar gerente</DialogTitle>
            <DialogDescription>
              O gerente terá acesso apenas às lojas selecionadas e poderá administrar parceiros vinculados a elas.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={createManager}>
            <div className="space-y-2">
              <Label htmlFor="manager-name">Nome completo</Label>
              <Input
                id="manager-name"
                value={managerForm.nome}
                onChange={(event) => setManagerForm({ ...managerForm, nome: event.target.value })}
                className="input-field"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manager-email">Email</Label>
              <Input
                id="manager-email"
                type="email"
                value={managerForm.email}
                onChange={(event) => setManagerForm({ ...managerForm, email: event.target.value })}
                className="input-field"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manager-password">Senha inicial</Label>
              <Input
                id="manager-password"
                type="password"
                minLength={6}
                value={managerForm.password}
                onChange={(event) => setManagerForm({ ...managerForm, password: event.target.value })}
                className="input-field"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Lojas permitidas</Label>
              <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
                {lojas.map((loja) => (
                  <Label key={loja.id} htmlFor={`manager-store-${loja.id}`} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50">
                    <Checkbox
                      id={`manager-store-${loja.id}`}
                      checked={managerForm.loja_ids.includes(loja.id)}
                      onCheckedChange={() => toggleManagerStore(loja.id)}
                    />
                    <span className="text-sm text-slate-700">{loja.nome}</span>
                  </Label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setManagerDialogOpen(false)} disabled={savingManager}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" disabled={savingManager}>
                {savingManager ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                <span className="ml-2">Liberar gerente</span>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!roleDialogUser} onOpenChange={(open) => !open && setRoleDialogUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Perfil administrativo</DialogTitle>
            <DialogDescription>
              Defina se esta conta pode liberar gerentes ou atua somente como gerente das lojas vinculadas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
              <p className="font-medium text-slate-900">{roleDialogUser?.nome}</p>
              <p className="text-sm text-slate-500">{roleDialogUser?.email}</p>
            </div>
            <Label htmlFor="admin-role">Perfil</Label>
            <select
              id="admin-role"
              value={selectedAdminRole}
              onChange={(event) => setSelectedAdminRole(event.target.value)}
              className="input-field w-full"
            >
              <option value="superior">Administrador superior</option>
              <option value="gerente">Gerente de loja</option>
            </select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRoleDialogUser(null)} disabled={savingAdminRole}>
              Cancelar
            </Button>
            <Button type="button" className="btn-primary" onClick={saveAdministrativeRole} disabled={savingAdminRole}>
              {savingAdminRole ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              <span className="ml-2">Salvar perfil</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContas;
